import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ErroApi } from '../lib/api';
import { Alerta, Botao, Campo, Cartao } from '../componentes/Interface';

type MinisterioAberto = { chave: string; nome: string };

/** Uma frase curta por ministerio, so para a pessoa se reconhecer na lista. */
const EXPLICACAO: Record<string, string> = {
  louvor: 'toco ou canto nos cultos',
  midia: 'câmera, som, projeção ou transmissão',
  recepcao: 'recebo quem chega na porta',
  cafeteria: 'sirvo o café antes e depois do culto',
  kids: 'cuido das crianças',
  limpeza_pos_culto: 'arrumo a igreja depois do culto',
  limpeza_semanal: 'cuido da casa durante a semana',
  abertura_culto: 'abro o culto, oração e avisos',
  profetico: 'intercessão e ministração',
};

export default function Cadastro() {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [senha, setSenha] = useState('');
  const [participacao, setParticipacao] = useState('membro');
  const [ministerios, setMinisterios] = useState<MinisterioAberto[]>([]);
  const [serve, setServe] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let vivo = true;
    api<{ ministerios: MinisterioAberto[] }>('/cadastro/ministerios')
      .then((d) => vivo && setMinisterios(d.ministerios))
      // ARMADILHA 12: se a lista nao vier, a pessoa ainda consegue se cadastrar
      // como membro — mas o motivo aparece, nao some
      .catch(() => vivo && setErro('Não consegui carregar a lista de ministérios.'));
    return () => {
      vivo = false;
    };
  }, []);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (serve && participacao === 'membro') {
      setErro('Escolha em qual ministério você serve.');
      return;
    }
    setEnviando(true);
    try {
      const r = await api<{ mensagem: string }>('/cadastro', {
        method: 'POST',
        body: JSON.stringify({
          nome: nome.trim(),
          email: email.trim(),
          telefone: telefone.trim() || null,
          senha,
          participacao,
          mensagem: mensagem.trim() || null,
        }),
      });
      setPronto(r.mensagem);
    } catch (e) {
      // ARMADILHA 12: o motivo aparece, nao some
      setErro(e instanceof ErroApi ? e.message : 'Não consegui concluir o cadastro.');
    } finally {
      setEnviando(false);
    }
  }

  if (pronto) {
    return (
      <div className="flex min-h-full items-center justify-center bg-fundo px-4 py-12">
        <div className="w-full max-w-sm text-center">
          <img
            src="/logo-fundo.png"
            alt="Sara Osasco"
            className="mx-auto h-20 w-20 rounded-2xl object-cover shadow-cartao"
          />
          <div className="mt-6">
            <Cartao>
              <Alerta tipo="ok">{pronto}</Alerta>
              <Link
                to="/login"
                className="mt-4 block rounded-xl bg-tinta-900 px-4 py-2.5 font-semibold text-white"
              >
                Entrar agora
              </Link>
            </Cartao>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-fundo px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="mb-6 flex flex-col items-center text-center">
          <img
            src="/logo-fundo.png"
            alt="Sara Osasco"
            className="h-20 w-20 rounded-2xl object-cover shadow-cartao"
          />
          <h1 className="mt-3 text-xl font-bold text-tinta-900">Criar meu acesso</h1>
          <p className="mt-1 text-sm text-tinta-400">
            Leva um minuto. Depois é só entrar com seu e-mail e senha.
          </p>
        </div>

        <Cartao>
          {erro && (
            <div className="mb-4">
              <Alerta>{erro}</Alerta>
            </div>
          )}

          <form onSubmit={enviar} className="space-y-4">
            <Campo
              rotulo="Nome completo"
              required
              value={nome}
              onChange={(e) => setNome(e.target.value)}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="E-mail"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Campo
                rotulo="WhatsApp (opcional)"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                placeholder="(11) 90000-0000"
              />
            </div>
            <Campo
              rotulo="Senha"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="pelo menos 8 caracteres"
            />

            <fieldset>
              <legend className="mb-2 text-sm font-semibold text-tinta-700">
                Como você participa da igreja?
              </legend>
              <div className="space-y-2">
                {[
                  {
                    serve: false,
                    titulo: 'Sou membro da igreja',
                    texto: 'Acompanho as palavras, as fotos e o calendário dos cultos.',
                  },
                  {
                    serve: true,
                    titulo: 'Sirvo em um ministério',
                    texto: 'Preciso ver a escala do meu ministério e confirmar presença.',
                  },
                ].map((o) => (
                  <label
                    key={String(o.serve)}
                    className={`flex cursor-pointer gap-3 rounded-xl border p-3 transition ${
                      serve === o.serve
                        ? 'border-tinta-900 bg-tinta-900/5'
                        : 'border-tinta-200 hover:bg-tinta-100'
                    }`}
                  >
                    <input
                      type="radio"
                      name="participacao"
                      checked={serve === o.serve}
                      onChange={() => {
                        setServe(o.serve);
                        setParticipacao('membro');
                      }}
                      className="mt-1"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-tinta-900">{o.titulo}</span>
                      <span className="block text-xs text-tinta-400">{o.texto}</span>
                    </span>
                  </label>
                ))}
              </div>

              {serve && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {ministerios.map((m) => (
                    <button
                      type="button"
                      key={m.chave}
                      onClick={() => setParticipacao(m.chave)}
                      className={`rounded-xl border p-3 text-left transition ${
                        participacao === m.chave
                          ? 'border-tinta-900 bg-tinta-100'
                          : 'border-tinta-200 hover:bg-tinta-100'
                      }`}
                    >
                      <span className="block text-sm font-semibold text-tinta-900">{m.nome}</span>
                      <span className="block text-xs text-tinta-400">
                        {EXPLICACAO[m.chave] ?? 'sirvo neste ministério'}
                      </span>
                    </button>
                  ))}
                  {ministerios.length === 0 && (
                    <p className="text-xs text-tinta-400">
                      A lista de ministérios não carregou. Você pode se cadastrar como membro e
                      pedir para a liderança te incluir depois.
                    </p>
                  )}
                </div>
              )}
            </fieldset>

            {serve && participacao !== 'membro' && (
              <>
                <Campo
                  rotulo="Conte um pouco (opcional)"
                  value={mensagem}
                  onChange={(e) => setMensagem(e.target.value)}
                  placeholder="Ex.: toco teclado desde 2023, converso com o ministro"
                />
                <Alerta tipo="aviso">
                  A liderança precisa aprovar. Você já entra e vê palavras, fotos e calendário; a
                  área do ministério aparece assim que aprovarem.
                </Alerta>
              </>
            )}

            <Botao type="submit" carregando={enviando} className="w-full">
              Criar meu acesso
            </Botao>
          </form>

          <p className="mt-4 text-center text-sm text-tinta-400">
            Já tem conta?{' '}
            <Link to="/login" className="font-medium text-tinta-900 hover:underline">
              Entrar
            </Link>
          </p>
        </Cartao>
      </div>
    </div>
  );
}
